import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import headerLogo from "../assets/sahajink-logo.png";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("");
  const navbarRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setScrolled(scrollY > 60);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (navbarRef.current && !navbarRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);

  // Body Scroll Lock
  useEffect(() => {
    const html = document.documentElement;
    if (menuOpen) {
      html.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    } else {
      html.style.overflow = "";
      document.body.style.overflow = "";
    }
    return () => {
      html.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Active section highlight (Only for Home page hash links)
  useEffect(() => {
    if (location.pathname !== "/") {
      setActive("");
      return;
    }

    const sections = document.querySelectorAll("section[id]");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(entry.target.id);
          }
        });
      },
      {
        rootMargin: "-40% 0px -55% 0px",
      },
    );

    sections.forEach((s) => observer.observe(s));

    return () => observer.disconnect();
  }, [location.pathname]);

  const navItems = [
    { name: "About", to: "/about", isHash: false },
    { name: "Our Works", to: "/our-works", isHash: false },
    { name: "Why Us", to: "/why-choose-us", isHash: false },
    { name: "Our Process", to: "/our-process", isHash: false },
  ];

  const handleNavClick = (isHash) => {
    setMenuOpen(false);
    if (!isHash) {
      window.scrollTo(0, 0);
    }
  };

  return (
    <header
      ref={navbarRef}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    >
      {/* Glasy Top Bar Background */}
      <div
        className={`absolute inset-0 h-[80px] -z-10 transition-all duration-300 
        ${scrolled ? "bg-[#0a1628]/95 border-b border-blue-500/30" : "bg-[#0a1628]/85 border-b border-white/10"}
        backdrop-blur-xl`}
      />

      <div className="max-w-[1200px] mx-auto h-[80px] flex items-center justify-between px-6">
        {/* Logo */}
        <Link
          to="/"
          onClick={() => {
            window.scrollTo(0, 0);
            setMenuOpen(false);
          }}
          className="flex items-center gap-2 text-white"
        >
          <img
            src={headerLogo}
            alt="Header Logo"
            className="w-[130px] sm:w-[150px] md:w-[180px] h-auto"
          />
        </Link>

        {/* Desktop Menu */}
        <nav className="hidden md:flex items-center gap-2">
          {navItems.map((item) => {
            const isActive = item.isHash
              ? active === item.to.split("#")[1] && location.pathname === "/"
              : location.pathname === item.to;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => handleNavClick(item.isHash)}
                className={`px-3 py-2 text-sm font-medium rounded-md transition
                ${
                  isActive
                    ? "text-white bg-white/10 shadow-[0_0_15px_rgba(37,99,235,0.2)]"
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
              >
                {item.name}
              </Link>
            );
          })}

          <Link
            to="/contact"
            onClick={() => handleNavClick(false)}
            className="ml-2 px-6 py-2 text-sm font-bold bg-blue-600 text-white rounded-full hover:bg-blue-500 transition shadow-[0_0_20px_rgba(37,99,235,0.3)] hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transform hover:scale-105 active:scale-95 duration-300 uppercase tracking-widest"
          >
            Contact
          </Link>
        </nav>

        {/* Hamburger */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden flex flex-col gap-1.5 focus:outline-none"
        >
          <span
            className={`block w-6 h-[2px] bg-white transition-all duration-300 ${
              menuOpen ? "rotate-45 translate-y-[8px]" : ""
            }`}
          />
          <span
            className={`block w-6 h-[2px] bg-white transition-all duration-300 ${
              menuOpen ? "opacity-0" : ""
            }`}
          />
          <span
            className={`block w-6 h-[2px] bg-white transition-all duration-300 ${
              menuOpen ? "-rotate-45 -translate-y-[8px]" : ""
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-[80px] left-0 right-0 bg-[#0a1628]/70 backdrop-blur-xl transform transition-all duration-500 ease-in-out border-b border-white/10 
        ${menuOpen ? "translate-y-0 opacity-100 visible h-auto pb-8 shadow-2xl" : "-translate-y-5 opacity-0 invisible h-0"}`}
        style={{ WebkitBackdropFilter: "blur(24px)" }}
      >
        <div className="flex flex-col px-6 py-6 gap-6">
          {navItems.map((item) => {
            const isActive = item.isHash
              ? active === item.to.split("#")[1] && location.pathname === "/"
              : location.pathname === item.to;

            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => handleNavClick(item.isHash)}
                className={`text-lg font-medium tracking-wide flex items-center justify-between group transition-colors duration-300
                ${isActive ? "text-blue-500" : "text-white/80 hover:text-white"}`}
              >
                {item.name}
                <span
                  className={`w-2.5 h-2.5 rounded-full bg-blue-500 transition-all duration-300 shadow-[0_0_15px_#3b82f6]
                ${isActive ? "scale-100 opacity-100" : "scale-0 opacity-0 group-hover:scale-100 group-hover:opacity-100"}`}
                ></span>
              </Link>
            );
          })}

          <Link
            to="/contact"
            onClick={() => handleNavClick(false)}
            className="mt-4 px-4 py-4 bg-gradient-to-r from-blue-700 to-blue-500 rounded-xl text-white font-bold text-center uppercase tracking-[0.2em] shadow-lg active:scale-95 transition-all duration-300"
          >
            Get Expert Quote
          </Link>
        </div>
      </div>
    </header>
  );
}
